import React, { FC, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { BoxV2 as Box, Flex } from 'portal-commons';
import {
  faIndustryWindows,
  faDollar,
  faEuroSign,
  faComputer,
  faLaptop,
} from '@fortawesome/pro-regular-svg-icons';

import ContentBlockHeader from '@/components/ContentBlockHeader';
import PhoneInput from '@/applications/CspPortal/Campaigns/components/RcsCampaignDetailsForm/PhoneInput';
import { CspRegistrationFormData } from '../types';
import useContactDetailsControllers from '../hooks/useContactDetailsControllers';
import AddEmailBtn from './AddEmailBtn';
import Divider from './Divider';
import RemovableTextInput from './RemovableTextInput';
import StepFooter from './StepFooter';

interface StepContactDetailsProps {
  onBack: () => void;
}

const StepContactDetails: FC<StepContactDetailsProps> = ({ onBack }) => {
  const {
    control,
    formState: { isValid, isValidating },
    setValue,
  } = useFormContext<CspRegistrationFormData>();

  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  const {
    phone,
    setPhone,
    validatePhone,
    phoneError,
    market,
    emailArray,
    financeEmailArray,
    techEmailArray,
    escalateEmailArray,
    emailRules,
  } = useContactDetailsControllers(control);

  const isNextDisabled = isValidating || !isValid;

  const MAX_EMAIL_COUNT = 5;

  return (
    <Flex sx={{ flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 24 }}>
        {/* Commercial Contact Details */}
        <Box sx={{ pt: 24, pb: emailArray.fields.length < 5 ? 48 : 24 }}>
          <ContentBlockHeader
            icon={faIndustryWindows}
            title="Commercial Contact Details"
            subTitle="Used for contract and business communications."
            titleRowSx={{ height: '32px' }}
          />
          <Flex sx={{ gap: 24, mt: 24 }}>
            <Box sx={{ flex: 1, position: 'relative' }}>
              {(isPhoneFocused || !!phone) && (
                <Box
                  as="label"
                  sx={{
                    display: 'block',
                    mb: '8px',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: phoneError ? '#B70505' : '#00698F',
                    backgroundColor: '#FFFFFF',
                    padding: '0 4px',
                    position: 'absolute',
                    top: '-10px',
                    left: '52px',
                  }}
                >
                  Phone Number *
                </Box>
              )}
              <PhoneInput
                data-testid="cspContactDetailPhoneInput"
                placeholder="Phone Number *"
                value={phone}
                onChange={setPhone}
                onCountryChange={(country) => {
                  setValue('ui.phoneCountry', country || '');
                }}
                onFocus={() => setIsPhoneFocused(true)}
                onBlur={() => {
                  setIsPhoneFocused(false);
                  validatePhone();
                }}
                error={!!phoneError}
                note={phoneError?.message}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Flex sx={{ flexDirection: 'column', gap: 16 }}>
                {emailArray.fields.map((field, index) => {
                  const isLast = index === emailArray.fields.length - 1;
                  return (
                    <Controller
                      key={field.id}
                      control={control}
                      name={`contact.email.${index}.email`}
                      rules={emailRules}
                      render={({
                        field: controllerField,
                        fieldState: { error },
                      }) => (
                        <RemovableTextInput
                          data-testid="cspContactDetailEmailInput"
                          label="Email Address"
                          required
                          maxLength={100}
                          value={controllerField.value}
                          onChange={controllerField.onChange}
                          onBlur={controllerField.onBlur}
                          error={!!error}
                          note={error?.message}
                          removable={emailArray.fields.length > 1}
                          onRemoveClick={() => emailArray.remove(index)}
                        >
                          {isLast &&
                            emailArray.fields.length < MAX_EMAIL_COUNT && (
                              <AddEmailBtn
                                onClick={() => emailArray.append({ email: '' })}
                              />
                            )}
                        </RemovableTextInput>
                      )}
                    />
                  );
                })}
              </Flex>
            </Box>
          </Flex>
        </Box>

        <Divider />

        {/* Financial Contact Details */}
        <Box sx={{ pt: 24, pb: financeEmailArray.fields.length < 5 ? 48 : 24 }}>
          <ContentBlockHeader
            icon={market === 'DE' ? faEuroSign : faDollar}
            title="Financial Contact Details"
            subTitle="Used for invoicing and finance communications."
            titleRowSx={{ height: '32px' }}
          />
          <Flex sx={{ gap: 24, mt: 24 }}>
            <Box sx={{ flex: 1 }}>
              <Flex sx={{ flexDirection: 'column', gap: 16 }}>
                {financeEmailArray.fields.map((field, index) => {
                  const isLast = index === financeEmailArray.fields.length - 1;
                  return (
                    <Controller
                      key={field.id}
                      control={control}
                      name={`contact.financeEmail.${index}.email`}
                      rules={emailRules}
                      render={({
                        field: controllerField,
                        fieldState: { error },
                      }) => (
                        <RemovableTextInput
                          data-testid="cspFinanceEmailInput"
                          label="Email Address"
                          required
                          maxLength={100}
                          value={controllerField.value}
                          onChange={controllerField.onChange}
                          onBlur={controllerField.onBlur}
                          error={!!error}
                          note={error?.message}
                          removable={financeEmailArray.fields.length > 1}
                          onRemoveClick={() => financeEmailArray.remove(index)}
                        >
                          {isLast &&
                            financeEmailArray.fields.length <
                              MAX_EMAIL_COUNT && (
                              <AddEmailBtn
                                onClick={() =>
                                  financeEmailArray.append({ email: '' })
                                }
                              />
                            )}
                        </RemovableTextInput>
                      )}
                    />
                  );
                })}
              </Flex>
            </Box>
            <Box sx={{ flex: 1 }} />
          </Flex>
        </Box>

        <Divider />

        {/* System Notification Contact Details */}
        <Box
          sx={{ pt: 24, pb: escalateEmailArray.fields.length < 5 ? 48 : 24 }}
        >
          <ContentBlockHeader
            icon={faComputer}
            title="System Notification Contact Details"
            subTitle="Used for system event notifications."
            titleRowSx={{ height: '32px' }}
          />
          <Flex sx={{ gap: 24, mt: 24 }}>
            <Box sx={{ flex: 1 }}>
              <Flex sx={{ flexDirection: 'column', gap: 16 }}>
                {escalateEmailArray.fields.map((field, index) => {
                  const isLast = index === escalateEmailArray.fields.length - 1;
                  return (
                    <Controller
                      key={field.id}
                      control={control}
                      name={`contact.escalateEmail.${index}.email`}
                      rules={emailRules}
                      render={({
                        field: controllerField,
                        fieldState: { error },
                      }) => (
                        <RemovableTextInput
                          data-testid="cspEscalateEmailInput"
                          label="Email Address"
                          required
                          maxLength={100}
                          value={controllerField.value}
                          onChange={controllerField.onChange}
                          onBlur={controllerField.onBlur}
                          error={!!error}
                          note={error?.message}
                          removable={escalateEmailArray.fields.length > 1}
                          onRemoveClick={() => escalateEmailArray.remove(index)}
                        >
                          {isLast &&
                            escalateEmailArray.fields.length <
                              MAX_EMAIL_COUNT && (
                              <AddEmailBtn
                                onClick={() =>
                                  escalateEmailArray.append({ email: '' })
                                }
                              />
                            )}
                        </RemovableTextInput>
                      )}
                    />
                  );
                })}
              </Flex>
            </Box>
            <Box sx={{ flex: 1 }} />
          </Flex>
        </Box>

        <Divider />

        {/* Technical Contact Details */}
        <Box sx={{ pt: 24, pb: techEmailArray.fields.length < 5 ? 48 : 24 }}>
          <ContentBlockHeader
            icon={faLaptop}
            title="Technical Contact Details"
            subTitle="Used to communicate with your support team. Also serves as a fallback if other provided addresses do not respond to contact."
            titleRowSx={{ height: '32px' }}
          />
          <Flex sx={{ gap: 24, mt: 24 }}>
            <Box sx={{ flex: 1 }}>
              <Flex sx={{ flexDirection: 'column', gap: 16 }}>
                {techEmailArray.fields.map((field, index) => {
                  const isLast = index === techEmailArray.fields.length - 1;
                  return (
                    <Controller
                      key={field.id}
                      control={control}
                      name={`contact.techEmail.${index}.email`}
                      rules={emailRules}
                      render={({
                        field: controllerField,
                        fieldState: { error },
                      }) => (
                        <RemovableTextInput
                          data-testid="cspTechEmailInput"
                          label="Email Address"
                          required
                          maxLength={100}
                          value={controllerField.value}
                          onChange={controllerField.onChange}
                          onBlur={controllerField.onBlur}
                          error={!!error}
                          note={error?.message}
                          removable={techEmailArray.fields.length > 1}
                          onRemoveClick={() => techEmailArray.remove(index)}
                        >
                          {isLast &&
                            techEmailArray.fields.length < MAX_EMAIL_COUNT && (
                              <AddEmailBtn
                                onClick={() =>
                                  techEmailArray.append({ email: '' })
                                }
                              />
                            )}
                        </RemovableTextInput>
                      )}
                    />
                  );
                })}
              </Flex>
            </Box>
            <Box sx={{ flex: 1 }} />
          </Flex>
        </Box>
      </Box>

      <StepFooter
        onBack={onBack}
        nextLabel="Next: Terms & Conditions"
        nextDisabled={isNextDisabled}
      />
    </Flex>
  );
};

export default StepContactDetails;
